import React, { useEffect, useState, useCallback, useContext, useRef, useMemo } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { CSS } from '@dnd-kit/utilities'
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Trash2,
  Paperclip,
  Plus,
  CheckCircle,
} from 'lucide-react'
import axios from '../../../config/axios'
import { stageDisplayNumber } from '../../../pages/ClientPanel/ProductionFlow/stageNumbering'
import { secondsToHHmm } from '../../../utils/time'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import ConfirmationModal from '../../ModalConfirmation'
import * as S from './style'

interface StageRef {
  name: string
  id: number
}

interface MasterStage {
  id: number
  name: string
  description?: string
}

interface SavedStageData {
  id: number
  serviceId: number
  stageId: number
  completed: boolean
  startedAt?: string | null
  completedAt?: string | null
  index?: number
  parentId?: number | null
  description?: string
  observation?: string
  executionTime?: number | null
  setupTime?: number | null
  Stage?: StageRef
  stage?: StageRef
  Children?: SavedStageData[]
  FileInServiceStage?: any[]
}

export interface OpStageManagerProps {
  opId: number
  readOnly?: boolean
  isFashion?: boolean
}

function HHmmToSeconds(timeStr: string): number {
  if (!timeStr) return 0
  const parts = timeStr.split(':')
  if (parts.length < 2) return 0
  const h = parseInt(parts[0], 10) || 0
  const m = parseInt(parts[1], 10) || 0
  return h * 3600 + m * 60
}

interface SortableStageCardItemProps {
  sortableId: string
  stage: SavedStageData
  parentIndex: number
  childIndex?: number
  siblings: SavedStageData[]
  scope: 'root' | 'child'
  parentStageId?: number
  isChild: boolean
  availableStages: MasterStage[]
  expandedStageIds: Record<number, boolean>
  toggleExpand: (id: number) => void
  stageForms: Record<number, any>
  updateFormField: (stageId: number, field: string, value: any) => void
  handleSaveStage: (stage: SavedStageData) => void
  savingStageId: number | null
  setDeleteConfirm: (val: { isOpen: boolean; stageId: number | null }) => void
  handleAddSubstage: (stage: SavedStageData) => void
  moveSiblings: (
    siblings: SavedStageData[],
    idx: number,
    dir: 'up' | 'down',
    scope: 'root' | 'child',
    parentId?: number,
  ) => void
  uploadingStageId: number | null
  fileInputRefs: React.MutableRefObject<Record<number, HTMLInputElement | null>>
  handleFileUpload: (stageId: number, files: FileList | null) => void
  handleStageFileView: (serviceStageId: number, fileId: number) => void
  handleStageFileDownload: (serviceStageId: number, fileId: number, name: string) => void
  handleDeleteFile: (stageId: number, fileId: number) => void
  readOnly?: boolean
}

const SortableStageCardItem: React.FC<SortableStageCardItemProps> = ({
  sortableId,
  stage,
  parentIndex,
  childIndex,
  siblings,
  scope,
  parentStageId,
  isChild,
  availableStages,
  expandedStageIds,
  toggleExpand,
  stageForms,
  updateFormField,
  handleSaveStage,
  savingStageId,
  setDeleteConfirm,
  handleAddSubstage,
  moveSiblings,
  uploadingStageId,
  fileInputRefs,
  handleFileUpload,
  handleStageFileView,
  handleStageFileDownload,
  handleDeleteFile,
  readOnly = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: stage.completed || readOnly,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.4 : 1,
  }

  const idx = siblings.findIndex((s) => s.id === stage.id)
  const isExpanded = Boolean(expandedStageIds[stage.id])
  const labelNumber = stageDisplayNumber(parentIndex, childIndex)
  const stageName = stage.stage?.name || stage.Stage?.name || 'Etapa'
  const operationLabel = `ETAPA ${labelNumber} - ${stageName}`

  const stageStatus: 'completed' | 'inProgress' | 'pending' = stage.completed
    ? 'completed'
    : stage.startedAt
      ? 'inProgress'
      : 'pending'

  const stageStatusLabel =
    stageStatus === 'completed'
      ? 'Concluída'
      : stageStatus === 'inProgress'
        ? 'Em andamento'
        : 'Pendente'

  const canMoveUp = idx > 0 && !stage.completed && !readOnly
  const canMoveDown = idx < siblings.length - 1 && !stage.completed && !readOnly

  const formData = stageForms[stage.id] || {
    stageId: stage.stageId || stage.stage?.id || 0,
    description: stage.description || '',
    observation: stage.observation || '',
    executionTime: stage.executionTime ? secondsToHHmm(stage.executionTime) : '',
    setupTime: stage.setupTime ? secondsToHHmm(stage.setupTime) : '',
  }

  const stageFiles = stage.FileInServiceStage ?? []

  return (
    <div ref={setNodeRef} style={style}>
      <S.StageCard $completed={stage.completed} $isChild={isChild} $isDragging={isDragging}>
        <S.StageCardHeader>
          <S.StageHeaderLeft>
            {!stage.completed && !readOnly && (
              <S.DragHandle {...attributes} {...listeners} title="Arrastar para reordenar">
                <GripVertical size={16} />
              </S.DragHandle>
            )}
            <S.StageTitleText>{operationLabel}</S.StageTitleText>
            <S.StatusTag $variant={stageStatus}>{stageStatusLabel}</S.StatusTag>
          </S.StageHeaderLeft>

          <S.StageHeaderRight>
            <S.CircularButton
              type="button"
              onClick={() => toggleExpand(stage.id)}
              title={isExpanded ? 'Recolher' : 'Expandir'}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </S.CircularButton>

            {!readOnly && (
              <>
                <S.ActionIconButton
                  type="button"
                  disabled={!canMoveUp}
                  onClick={() => moveSiblings(siblings, idx, 'up', scope, parentStageId)}
                  title="Subir etapa"
                >
                  <ChevronUp size={18} />
                </S.ActionIconButton>
                <S.ActionIconButton
                  type="button"
                  disabled={!canMoveDown}
                  onClick={() => moveSiblings(siblings, idx, 'down', scope, parentStageId)}
                  title="Descer etapa"
                >
                  <ChevronDown size={18} />
                </S.ActionIconButton>
                <S.ActionIconButton
                  type="button"
                  onClick={() => setDeleteConfirm({ isOpen: true, stageId: stage.id })}
                  title="Excluir etapa"
                >
                  <Trash2 size={18} />
                </S.ActionIconButton>
              </>
            )}
          </S.StageHeaderRight>
        </S.StageCardHeader>

        {isExpanded && (
          <S.CardFormBody>
            <S.FormGrid>
              <S.FormGroup $fullWidth>
                <S.FormLabel>Serviço</S.FormLabel>
                <S.StyledSelect
                  value={formData.stageId}
                  disabled={readOnly || stage.completed}
                  onChange={(e) => updateFormField(stage.id, 'stageId', Number(e.target.value))}
                >
                  {availableStages.map((masterStage) => (
                    <option key={masterStage.id} value={masterStage.id}>
                      {masterStage.name}
                    </option>
                  ))}
                </S.StyledSelect>
              </S.FormGroup>

              <S.FormGroup>
                <S.FormLabel>Tempo de execução</S.FormLabel>
                <S.StyledInput
                  type="time"
                  disabled={readOnly || stage.completed}
                  value={formData.executionTime}
                  onChange={(e) => updateFormField(stage.id, 'executionTime', e.target.value)}
                />
              </S.FormGroup>

              <S.FormGroup>
                <S.FormLabel>Tempo de setup</S.FormLabel>
                <S.StyledInput
                  type="time"
                  disabled={readOnly || stage.completed}
                  value={formData.setupTime}
                  onChange={(e) => updateFormField(stage.id, 'setupTime', e.target.value)}
                />
              </S.FormGroup>

              <S.FormGroup $fullWidth>
                <S.LabelRow>
                  <S.FormLabel>Anexo</S.FormLabel>
                </S.LabelRow>
                {!readOnly && !stage.completed && (
                  <S.FileUploadArea
                    onClick={() => fileInputRefs.current[stage.id]?.click()}
                  >
                    <span>
                      {uploadingStageId === stage.id
                        ? 'Enviando arquivos...'
                        : 'Clique para adicionar arquivos'}
                    </span>
                    <Paperclip size={16} color="#0090d3" />
                    <input
                      type="file"
                      ref={(el) => (fileInputRefs.current[stage.id] = el)}
                      style={{ display: 'none' }}
                      multiple
                      onChange={(e) => handleFileUpload(stage.id, e.target.files)}
                    />
                  </S.FileUploadArea>
                )}

                {stageFiles.length > 0 && (
                  <S.FileList>
                    {stageFiles.map((fileItem: any) => {
                      const fileObj = fileItem.GeneralFile || fileItem
                      return (
                        <S.FileRow key={fileObj.id}>
                          <span>{fileObj.title || fileObj.name || 'Arquivo'}</span>
                          <S.FileActions>
                            <S.FileActionButton
                              type="button"
                              onClick={() => handleStageFileView(stage.id, fileObj.id)}
                            >
                              Visualizar
                            </S.FileActionButton>
                            <span style={{ color: '#ccc' }}>|</span>
                            <S.FileActionButton
                              type="button"
                              onClick={() =>
                                handleStageFileDownload(
                                  stage.id,
                                  fileObj.id,
                                  fileObj.title || 'arquivo',
                                )
                              }
                            >
                              Download
                            </S.FileActionButton>
                            {!readOnly && !stage.completed && (
                              <>
                                <span style={{ color: '#ccc' }}>|</span>
                                <S.FileActionButton
                                  type="button"
                                  onClick={() => handleDeleteFile(stage.id, fileObj.id)}
                                >
                                  Excluir
                                </S.FileActionButton>
                              </>
                            )}
                          </S.FileActions>
                        </S.FileRow>
                      )
                    })}
                  </S.FileList>
                )}
              </S.FormGroup>

              <S.FormGroup>
                <S.FormLabel>Descrição</S.FormLabel>
                <S.StyledTextarea
                  disabled={readOnly || stage.completed}
                  value={formData.description}
                  onChange={(e) => updateFormField(stage.id, 'description', e.target.value)}
                />
              </S.FormGroup>

              <S.FormGroup>
                <S.FormLabel>Observação</S.FormLabel>
                <S.StyledTextarea
                  disabled={readOnly || stage.completed}
                  value={formData.observation}
                  onChange={(e) => updateFormField(stage.id, 'observation', e.target.value)}
                />
              </S.FormGroup>
            </S.FormGrid>

            {!readOnly && !stage.completed && (
              <S.CardFooterRow>
                {!isChild ? (
                  <S.AddSubStageButton
                    type="button"
                    onClick={() => handleAddSubstage(stage)}
                  >
                    <Plus size={14} /> Adicionar subetapa
                  </S.AddSubStageButton>
                ) : (
                  <div />
                )}
                <S.SaveButton
                  type="button"
                  onClick={() => handleSaveStage(stage)}
                  disabled={savingStageId === stage.id}
                >
                  {savingStageId === stage.id ? 'Salvando...' : 'Salvar alterações'}
                </S.SaveButton>
              </S.CardFooterRow>
            )}
          </S.CardFormBody>
        )}
      </S.StageCard>

      {stage.Children && stage.Children.length > 0 && (
        <SortableContext
          items={stage.Children.map((c) => `child:${stage.id}:${c.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {stage.Children.map((child, cIdx) => (
            <SortableStageCardItem
              key={child.id}
              sortableId={`child:${stage.id}:${child.id}`}
              stage={child}
              parentIndex={parentIndex}
              childIndex={cIdx}
              siblings={stage.Children || []}
              scope="child"
              parentStageId={stage.id}
              isChild={true}
              availableStages={availableStages}
              expandedStageIds={expandedStageIds}
              toggleExpand={toggleExpand}
              stageForms={stageForms}
              updateFormField={updateFormField}
              handleSaveStage={handleSaveStage}
              savingStageId={savingStageId}
              setDeleteConfirm={setDeleteConfirm}
              handleAddSubstage={handleAddSubstage}
              moveSiblings={moveSiblings}
              uploadingStageId={uploadingStageId}
              fileInputRefs={fileInputRefs}
              handleFileUpload={handleFileUpload}
              handleStageFileView={handleStageFileView}
              handleStageFileDownload={handleStageFileDownload}
              handleDeleteFile={handleDeleteFile}
              readOnly={readOnly}
            />
          ))}
        </SortableContext>
      )}
    </div>
  )
}

export const OpStageManager: React.FC<OpStageManagerProps> = ({ opId, readOnly = false, isFashion = false }) => {
  const [rootStages, setRootStages] = useState<SavedStageData[]>([])
  const [availableStages, setAvailableStages] = useState<MasterStage[]>([])
  const [expandedStageIds, setExpandedStageIds] = useState<Record<number, boolean>>({})
  const [stageForms, setStageForms] = useState<Record<number, any>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [savingStageId, setSavingStageId] = useState<number | null>(null)
  const [uploadingStageId, setUploadingStageId] = useState<number | null>(null)
  const [isConfirmDefaultModalOpen, setIsConfirmDefaultModalOpen] = useState(false)
  const [defaultStageIds, setDefaultStageIds] = useState<number[] | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; stageId: number | null }>({
    isOpen: false,
    stageId: null,
  })

  const { setFeedback } = useContext(FeedbackContext)
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({})

  const fetchDefaultSequence = useCallback(async () => {
    if (!isFashion) return
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/fashion-production/default-sequence`,
      )
      if (Array.isArray(res.data)) {
        setDefaultStageIds(res.data)
      }
    } catch (err) {
      console.error('Erro ao buscar sequência padrão de Fashion:', err)
    }
  }, [isFashion])

  useEffect(() => {
    fetchDefaultSequence()
  }, [fetchDefaultSequence])

  const isSequenceDefault = useMemo(() => {
    if (!isFashion || !defaultStageIds || defaultStageIds.length === 0) return false
    const currentRootStageIds = rootStages.map((s) => s.stageId)
    if (currentRootStageIds.length !== defaultStageIds.length) return false
    return currentRootStageIds.every((id, idx) => id === defaultStageIds[idx])
  }, [isFashion, defaultStageIds, rootStages])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const fetchAvailableStages = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/stages`,
      )
      const allStages: MasterStage[] = response.data || []
      const productionOnlyStages = allStages.filter((s: any) => {
        const name = (s.name || '').trim()
        const nameLower = name.toLowerCase()
        const areaName = (s.Sector?.Area?.name || s.sector?.Area?.name || s.sector?.area?.name || s.Area?.name || '').toLowerCase()

        if (areaName) {
          if (
            areaName.includes('mostruá') ||
            areaName.includes('mostrua') ||
            areaName.includes('modelag') ||
            areaName.includes('pilotag') ||
            areaName.includes('desenvolvim')
          ) {
            return false
          }
        }

        const isMostruario = name.startsWith('(M)') || nameLower.includes('mostruário') || nameLower.includes('mostruario')
        const isPilotModelagem = name.startsWith('(P)') || nameLower.includes('modelagem') || nameLower.includes('pilotagem') || nameLower.includes('desenvolvimento')
        const isPrototipo = nameLower.startsWith('protótipo') || nameLower.startsWith('prototipo')
        const isRefName = nameLower.includes('referência') || nameLower.includes('referencia')

        return !isMostruario && !isPilotModelagem && !isPrototipo && !isRefName
      })
      setAvailableStages(productionOnlyStages)
    } catch (error) {
      console.error('Erro ao buscar lista mestre de etapas:', error)
    }
  }, [])

  const fetchServiceStages = useCallback(async () => {
    if (!opId) return
    setIsLoading(true)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/services-of-op/${opId}`,
      )
      const raw = response.data || []
      const normalized = raw.map((root: SavedStageData) => ({
        ...root,
        stage: root.stage ?? {
          id: (root as { Stage?: StageRef }).Stage?.id ?? 0,
          name: (root as { Stage?: StageRef }).Stage?.name ?? '',
        },
        Children: (root.Children ?? []).map((child) => ({
          ...child,
          stage: child.stage ?? {
            id: (child as { Stage?: StageRef }).Stage?.id ?? 0,
            name: (child as { Stage?: StageRef }).Stage?.name ?? '',
          },
        })),
      }))
      setRootStages(normalized)

      const initialForms: Record<number, any> = {}
      const populateForms = (items: SavedStageData[]) => {
        items.forEach((item) => {
          initialForms[item.id] = {
            stageId: item.stageId || item.stage?.id || item.Stage?.id || 0,
            description: item.description || '',
            observation: item.observation || '',
            executionTime: item.executionTime ? secondsToHHmm(item.executionTime) : '',
            setupTime: item.setupTime ? secondsToHHmm(item.setupTime) : '',
          }
          if (item.Children?.length) {
            populateForms(item.Children)
          }
        })
      }
      populateForms(normalized)
      setStageForms(initialForms)
    } catch (error) {
      console.error('Erro ao buscar etapas salvas da OP:', error)
      setRootStages([])
    } finally {
      setIsLoading(false)
    }
  }, [opId])

  useEffect(() => {
    fetchAvailableStages()
    fetchServiceStages()
  }, [fetchAvailableStages, fetchServiceStages])

  const toggleExpand = (stageId: number) => {
    setExpandedStageIds((prev) => ({
      ...prev,
      [stageId]: !prev[stageId],
    }))
  }

  const updateFormField = (stageId: number, field: string, value: any) => {
    setStageForms((prev) => ({
      ...prev,
      [stageId]: {
        ...prev[stageId],
        [field]: value,
      },
    }))
  }

  const handleSaveStage = async (stage: SavedStageData) => {
    const formData = stageForms[stage.id]
    if (!formData) return

    setSavingStageId(stage.id)
    try {
      await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/edit-stage/${stage.id}`,
        {
          id: stage.id,
          stageId: Number(formData.stageId),
          description: formData.description,
          observation: formData.observation,
          executionTime: HHmmToSeconds(formData.executionTime),
          setupTime: HHmmToSeconds(formData.setupTime),
          parentId: stage.parentId ?? null,
        },
      )
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Etapa atualizada com sucesso!',
      })
      fetchServiceStages()
    } catch (error) {
      console.error('Erro ao salvar alteração da etapa:', error)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Falha ao salvar alterações da etapa.',
      })
    } finally {
      setSavingStageId(null)
    }
  }

  const handleDeleteStage = async () => {
    const stageId = deleteConfirm.stageId
    if (!stageId) return

    try {
      await axios.delete(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/delete-stage/${stageId}`,
      )
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Etapa excluída com sucesso!',
      })
      setDeleteConfirm({ isOpen: false, stageId: null })
      fetchServiceStages()
    } catch (error) {
      console.error('Erro ao excluir etapa:', error)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Falha ao excluir etapa.',
      })
    }
  }

  const handleAddRootStage = async () => {
    const defaultStageId = availableStages[0]?.id || 1
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${opId}/stage`,
        {
          stageId: defaultStageId,
          description: '',
          observation: '',
        },
      )
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Nova etapa adicionada à OP!',
      })
      await fetchServiceStages()
      if (response.data?.id) {
        setExpandedStageIds((prev) => ({ ...prev, [response.data.id]: true }))
      }
    } catch (error) {
      console.error('Erro ao adicionar etapa:', error)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Falha ao adicionar etapa à OP.',
      })
    }
  }

  const handleAddSubstage = async (parentStage: SavedStageData) => {
    const defaultStageId = availableStages[0]?.id || 1
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${opId}/stage`,
        {
          stageId: defaultStageId,
          parentId: parentStage.id,
          description: '',
          observation: '',
        },
      )
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Subetapa adicionada com sucesso!',
      })
      await fetchServiceStages()
      if (response.data?.id) {
        setExpandedStageIds((prev) => ({
          ...prev,
          [parentStage.id]: true,
          [response.data.id]: true,
        }))
      }
    } catch (error) {
      console.error('Erro ao adicionar subetapa:', error)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Falha ao adicionar subetapa.',
      })
    }
  }

  const moveSiblings = async (
    siblings: SavedStageData[],
    currentIndex: number,
    direction: 'up' | 'down',
    scope: 'root' | 'child',
    parentStageId?: number,
  ) => {
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (newIndex < 0 || newIndex >= siblings.length) return
    const reordered = [...siblings]
    const [item] = reordered.splice(currentIndex, 1)
    reordered.splice(newIndex, 0, item)
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/move-stage`,
        {
          scope,
          parentStageId: scope === 'child' ? parentStageId : undefined,
          servicesStages: reordered.map((stage, index) => ({ id: stage.id, index })),
        },
      )
      if (response.data) {
        fetchServiceStages()
      }
    } catch (error) {
      console.error('Erro ao reordenar etapas:', error)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Falha ao reordenar a etapa.',
      })
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeIdStr = String(active.id)
    const overIdStr = String(over.id)

    if (activeIdStr.startsWith('root:') && overIdStr.startsWith('root:')) {
      const activeStageId = Number(activeIdStr.replace('root:', ''))
      const overStageId = Number(overIdStr.replace('root:', ''))

      const oldIndex = rootStages.findIndex((s) => s.id === activeStageId)
      const newIndex = rootStages.findIndex((s) => s.id === overStageId)

      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(rootStages, oldIndex, newIndex)
        setRootStages(reordered)

        try {
          await axios.post(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/move-stage`,
            {
              scope: 'root',
              servicesStages: reordered.map((stage, index) => ({ id: stage.id, index })),
            },
          )
        } catch (error) {
          console.error('Erro ao mover etapa via drag & drop:', error)
          fetchServiceStages()
        }
      }
    } else if (activeIdStr.startsWith('child:') && overIdStr.startsWith('child:')) {
      const partsActive = activeIdStr.split(':')
      const partsOver = overIdStr.split(':')
      const parentIdActive = Number(partsActive[1])
      const parentIdOver = Number(partsOver[1])

      if (parentIdActive === parentIdOver) {
        const activeChildId = Number(partsActive[2])
        const overChildId = Number(partsOver[2])

        const parent = rootStages.find((s) => s.id === parentIdActive)
        if (parent && parent.Children) {
          const oldIndex = parent.Children.findIndex((c) => c.id === activeChildId)
          const newIndex = parent.Children.findIndex((c) => c.id === overChildId)

          if (oldIndex !== -1 && newIndex !== -1) {
            const reorderedChildren = arrayMove(parent.Children, oldIndex, newIndex)
            const updatedRoots = rootStages.map((r) =>
              r.id === parentIdActive ? { ...r, Children: reorderedChildren } : r,
            )
            setRootStages(updatedRoots)

            try {
              await axios.post(
                `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/move-stage`,
                {
                  scope: 'child',
                  parentStageId: parentIdActive,
                  servicesStages: reorderedChildren.map((stage, index) => ({
                    id: stage.id,
                    index,
                  })),
                },
              )
            } catch (error) {
              console.error('Erro ao mover subetapa via drag & drop:', error)
              fetchServiceStages()
            }
          }
        }
      }
    }
  }

  const handleFileUpload = async (stageId: number, files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploadingStageId(stageId)

    const formData = new FormData()
    Array.from(files).forEach((file) => {
      formData.append('files', file)
    })

    try {
      await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/stage/${stageId}/upload-files`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Arquivo(s) enviado(s) com sucesso!',
      })
      fetchServiceStages()
    } catch (error) {
      console.error('Erro ao fazer upload de arquivo da etapa:', error)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Falha ao enviar arquivo.',
      })
    } finally {
      setUploadingStageId(null)
    }
  }

  const handleDeleteFile = async (stageId: number, fileId: number) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/stage/${stageId}/files/${fileId}`,
      )
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Arquivo removido da etapa.',
      })
      fetchServiceStages()
    } catch (error) {
      console.error('Erro ao remover arquivo:', error)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Falha ao remover arquivo.',
      })
    }
  }

  const handleStageFileDownload = async (serviceStageId: number, fileId: number, fileName: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/stage/${serviceStageId}/files/${fileId}/download`,
      )
      const { url } = response.data
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao baixar o arquivo.',
      })
    }
  }

  const handleStageFileView = async (serviceStageId: number, fileId: number) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/stage/${serviceStageId}/files/${fileId}/download?view=true`,
      )
      const { url } = response.data
      window.open(url, '_blank')
    } catch (error) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Erro ao abrir o arquivo.',
      })
    }
  }

  const handleSetDefaultSequence = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/fashion-production/${opId}/set-default-sequence`,
      )
      setFeedback({
        isOpen: true,
        message: 'Sequência e serviços definidos como padrão para novas OPs com sucesso!',
        statusOk: true,
      })
      setIsConfirmDefaultModalOpen(false)
      fetchDefaultSequence()
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Erro ao definir sequência padrão.'
      setFeedback({
        isOpen: true,
        message: msg,
        statusOk: false,
      })
      setIsConfirmDefaultModalOpen(false)
    }
  }

  if (isLoading && rootStages.length === 0) {
    return (
      <S.Section>
        <S.SectionHeader>
          <S.SectionTitle>Etapas de Produção</S.SectionTitle>
        </S.SectionHeader>
        <S.EmptyMessage>Carregando etapas da ordem de produção...</S.EmptyMessage>
      </S.Section>
    )
  }

  return (
    <S.Section>
      <S.SectionHeader>
        <S.SectionTitle>Etapas de Produção</S.SectionTitle>
        {!readOnly && (
          <S.ButtonGroup>
            {isFashion && (
              isSequenceDefault ? (
                <S.DefaultSequenceBadge>
                  <CheckCircle size={14} /> Serviços e ordem padrão
                </S.DefaultSequenceBadge>
              ) : (
                <S.SetDefaultButton type="button" onClick={() => setIsConfirmDefaultModalOpen(true)}>
                  <CheckCircle size={14} /> Definir serviços e ordem como padrão
                </S.SetDefaultButton>
              )
            )}
            <S.AddRootButton type="button" onClick={handleAddRootStage}>
              <Plus size={14} /> Adicionar serviço
            </S.AddRootButton>
          </S.ButtonGroup>
        )}
      </S.SectionHeader>

      {rootStages.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={rootStages.map((s) => `root:${s.id}`)}
            strategy={verticalListSortingStrategy}
          >
            {rootStages.map((root, parentIndex) => (
              <SortableStageCardItem
                key={root.id}
                sortableId={`root:${root.id}`}
                stage={root}
                parentIndex={parentIndex}
                siblings={rootStages}
                scope="root"
                isChild={false}
                availableStages={availableStages}
                expandedStageIds={expandedStageIds}
                toggleExpand={toggleExpand}
                stageForms={stageForms}
                updateFormField={updateFormField}
                handleSaveStage={handleSaveStage}
                savingStageId={savingStageId}
                setDeleteConfirm={setDeleteConfirm}
                handleAddSubstage={handleAddSubstage}
                moveSiblings={moveSiblings}
                uploadingStageId={uploadingStageId}
                fileInputRefs={fileInputRefs}
                handleFileUpload={handleFileUpload}
                handleStageFileView={handleStageFileView}
                handleStageFileDownload={handleStageFileDownload}
                handleDeleteFile={handleDeleteFile}
                readOnly={readOnly}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        <S.EmptyMessage>Nenhuma etapa cadastrada nesta ordem de produção.</S.EmptyMessage>
      )}

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        title="Excluir Etapa"
        message="Tem certeza que deseja excluir esta etapa da ordem de produção?"
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setDeleteConfirm({ isOpen: false, stageId: null }),
            variant: 'outline',
          },
          {
            label: 'Excluir',
            onClick: handleDeleteStage,
            variant: 'primary',
          },
        ]}
      />

      <ConfirmationModal
        isOpen={isConfirmDefaultModalOpen}
        title="Definir Serviços e Ordem como Padrão"
        message="Deseja definir os serviços e a ordem atual desta OP como padrão para todas as novas OPs de Fashion? As OPs já existentes não serão alteradas."
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setIsConfirmDefaultModalOpen(false),
            variant: 'outline',
          },
          {
            label: 'Confirmar e Definir',
            onClick: handleSetDefaultSequence,
            variant: 'primary',
          },
        ]}
      />
    </S.Section>
  )
}

export default OpStageManager
